import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus, FiTrash2, FiKey, FiCopy, FiCheck, FiRefreshCw, FiSend } from "react-icons/fi";
import { apiRequest } from "../../api";
import { PUBLIC_API_BASE, codeSamples } from "../../chatbot/chat";
import { SPEECH_LANGUAGES } from "../../video/subs/languages";
import Answer from "../../components/chatbot/Answer";
import { InsightsPanel, KnowledgePanel, TicketsPanel } from "./SupportPanels";
import { FunnelPanel, LeadsPanel, MeetingsPanel, SalesSettings } from "./SalesPanels";
import ChannelsPanel from "./ChannelsPanel";

const field = "w-full bg-navy border border-navy-border rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-light";
const label = "block text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1.5";
const btn = "px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-xs text-gray-200 inline-flex items-center gap-1.5 hover:border-accent-light disabled:opacity-40";
const panel = "bg-navy border border-navy-border rounded-xl p-5";
const EMPTY = { name: "", client_name: "", instructions: "", greeting: "Hello! How can I help you?", language: "auto", temperature: 0.5, max_reply_tokens: 600, history_turns: 12, allowed_origins: "", accent_color: "#2563eb", per_minute_limit: 30, daily_limit: 1000, is_active: true, is_demo: false, kind: "general", sales: {} };
const FIELDS = Object.keys(EMPTY);

function Copy({ text, children = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className={btn} onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* select by hand */ } }}>
      {done ? <><FiCheck /> Copied</> : <><FiCopy /> {children}</>}
    </button>
  );
}

function Keys({ bot, reload }) {
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    const { ok, data } = await apiRequest(`/core/admin/chatbots/${bot.id}/keys/`, { method: "POST", auth: true, body: { label: name } });
    setBusy(false);
    if (ok) { setFresh(data); setName(""); reload(); }
  };
  const revoke = async (key) => {
    if (!window.confirm(`Revoke the key "${key.label || key.prefix}"? Whoever uses it will stop working immediately.`)) return;
    await apiRequest(`/core/admin/chatbots/${bot.id}/keys/${key.id}/`, { method: "DELETE", auth: true });
    reload();
  };
  return (
    <div className={panel}>
      <h3 className="font-semibold mb-1 flex items-center gap-2"><FiKey /> API keys for the client</h3>
      <p className="text-xs text-gray-400 mb-4">Give a client a secret key. It goes on their server only. Make one per client or app, so you can revoke one without touching the others.</p>
      <div className="flex gap-2 mb-4">
        <input className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name of the client or app (e.g. Acme mobile app)" maxLength={80} />
        <button type="button" className={`${btn} shrink-0`} onClick={create} disabled={busy}><FiPlus /> Create key</button>
      </div>
      {fresh && (
        <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm" role="status">
          <div className="font-semibold text-emerald-200 mb-1">Copy this key now - it is not shown again.</div>
          <div className="flex items-center gap-2"><code className="break-all text-emerald-100 flex-1">{fresh.key}</code><Copy text={fresh.key} /></div>
        </div>
      )}
      <ul className="space-y-2">
        {bot.keys.length === 0 && <li className="text-sm text-gray-500">No keys yet.</li>}
        {bot.keys.map((k) => (
          <li key={k.id} className="flex items-center justify-between gap-3 text-sm bg-white/5 rounded-lg px-3 py-2">
            <div className="min-w-0">
              <span className={k.revoked ? "line-through text-gray-500" : ""}>{k.label || "Unnamed key"}</span>
              <span className="text-gray-500 font-mono text-xs ml-2">{k.prefix}...</span>
              <div className="text-[11px] text-gray-500">{k.revoked ? "Revoked" : k.last_used_at ? `Last used ${new Date(k.last_used_at).toLocaleString()}` : "Never used"}</div>
            </div>
            {!k.revoked && <button type="button" className={btn} onClick={() => revoke(k)}><FiTrash2 /> Revoke</button>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TestChat({ bot, status }) {
  const [log, setLog] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [provider, setProvider] = useState("");
  const send = async (e) => {
    e.preventDefault();
    const message = text.trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setLog((l) => [...l, { role: "user", text: message }]);
    const { ok, data } = await apiRequest(`/core/admin/chatbots/${bot.id}/test/`, { method: "POST", auth: true, body: { message, conversation_id: conversation, provider: provider || undefined } });
    setBusy(false);
    if (ok) { setConversation(data.conversation_id); setLog((l) => [...l, { role: "assistant", text: data.reply, note: `${data.provider} - ${(data.milliseconds / 1000).toFixed(1)} s` }]); }
    else setLog((l) => [...l, { role: "assistant", text: (data && data.error && data.error.message) || "The request failed.", error: true }]);
  };
  return (
    <div className={panel}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold">Try it (no key needed)</h3>
        <div className="flex items-center gap-2">
          <select className="bg-navy border border-navy-border rounded-lg text-xs px-2 py-1.5" value={provider} onChange={(e) => setProvider(e.target.value)} aria-label="AI model to use">
            <option value="">Best available</option>
            {(status ? status.providers : []).map((p) => <option key={p.name} value={p.name}>{p.name} ({p.model})</option>)}
          </select>
          <button type="button" className={btn} onClick={() => { setLog([]); setConversation(null); }}>New conversation</button>
        </div>
      </div>
      <div className="h-64 overflow-y-auto space-y-2 mb-3 pr-1" role="log">
        {log.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-brand-gradient" : m.error ? "bg-red-500/15 text-red-100" : "bg-white/10"}`}>
              <Answer text={m.text} />{m.note && <div className="text-[10px] text-gray-400 mt-1">{m.note}</div>}
            </div>
          </div>
        ))}
        {busy && <div className="text-xs text-gray-400">Thinking...</div>}
      </div>
      <form onSubmit={send} className="flex gap-2">
        <input className={field} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask the chatbot something..." aria-label="Test question" />
        <button type="submit" className={`${btn} shrink-0`} disabled={busy || !text.trim()}><FiSend /> Send</button>
      </form>
    </div>
  );
}

function Conversations({ bot }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let alive = true;
    setOpen(null);
    apiRequest(`/core/admin/chatbots/${bot.id}/conversations/`, { auth: true }).then(({ ok, data }) => { if (alive) setRows(ok ? data : []); });
    return () => { alive = false; };
  }, [bot.id]);
  const show = async (row) => {
    const { ok, data } = await apiRequest(`/core/admin/chatbots/${bot.id}/conversations/${row.id}/`, { auth: true });
    if (ok) setOpen(data);
  };
  return (
    <div className={panel}>
      <h3 className="font-semibold mb-3">Recent conversations</h3>
      {rows === null && <p className="text-sm text-gray-500">Loading...</p>}
      {rows && !rows.length && <p className="text-sm text-gray-500">Nothing has been asked yet.</p>}
      <ul className="space-y-1.5 mb-3">
        {(rows || []).map((r) => (
          <li key={r.id}>
            <button type="button" onClick={() => show(r)} className="w-full text-left text-sm bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2">
              <span className="text-gray-200">{r.last_question || "(no question)"}</span>
              <span className="block text-[11px] text-gray-500">{r.source} - {r.messages} messages - {new Date(r.updated_at).toLocaleString()}</span>
            </button>
          </li>
        ))}
      </ul>
      {open && (
        <div className="border-t border-navy-border pt-3 space-y-2 max-h-80 overflow-y-auto">
          {open.messages.map((m) => (
            <div key={m.id} className="text-sm"><span className={`text-xs font-semibold ${m.role === "user" ? "text-accent-light" : "text-emerald-300"}`}>{m.role === "user" ? `Customer${m.via === "voice" ? " (voice)" : ""}` : "Chatbot"}: </span><Answer text={m.text} /></div>
          ))}
        </div>
      )}
    </div>
  );
}

// The owner's side of the chatbots: make a chatbot for a client, tell it about the business, give out API keys and the chat-window line, try it, and read what was asked.
export default function ChatbotsAdmin() {
  const [bots, setBots] = useState(null);
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(null);       // id, or "new"
  const [form, setForm] = useState(EMPTY);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest("/core/admin/chatbots/", { auth: true });
    setBots(ok ? (data.results || data) : []);
  }, []);
  useEffect(() => { load(); apiRequest("/core/admin/chatbots/status/", { auth: true }).then(({ ok, data }) => setStatus(ok ? data : null)); }, [load]);

  const bot = useMemo(() => (bots || []).find((b) => b.id === selected) || null, [bots, selected]);
  useEffect(() => {
    if (bot) setForm(Object.fromEntries(FIELDS.map((f) => [f, bot[f]])));
    else if (selected === "new") setForm(EMPTY);
    setMessage("");
  }, [bot, selected]);
  useEffect(() => { if (bots && bots.length && selected === null) setSelected(bots[0].id); }, [bots, selected]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    const creating = selected === "new";
    const { ok, data } = await apiRequest(creating ? "/core/admin/chatbots/" : `/core/admin/chatbots/${selected}/`, { method: creating ? "POST" : "PATCH", auth: true, body: form });
    setSaving(false);
    if (!ok) { setMessage(Object.values(data || {}).flat().join(" ") || "Could not save."); return; }
    setMessage("Saved.");
    await load();
    if (creating) setSelected(data.id);
  };
  const remove = async () => {
    if (!window.confirm(`Delete the chatbot "${bot.name}" with all its keys and conversations? Its clients will stop working.`)) return;
    await apiRequest(`/core/admin/chatbots/${bot.id}/`, { method: "DELETE", auth: true });
    setSelected(null);
    load();
  };
  const newWidgetKey = async () => {
    if (!window.confirm("Make a new widget key? The chat windows already on websites stop working until their line is updated.")) return;
    await apiRequest(`/core/admin/chatbots/${bot.id}/regenerate-widget-key/`, { method: "POST", auth: true });
    load();
  };

  const samples = bot ? codeSamples(PUBLIC_API_BASE, "YOUR_API_KEY", bot.widget_key) : null;
  const onlyFree = status && status.providers.length > 0 && status.providers.every((p) => p.name === "free");
  const none = status && status.providers.length === 0;

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">AI Chatbots</h1>
      <p className="text-sm text-gray-400 mb-5">Make a chatbot for each client, tell it about their business, and give them an API key or a one-line chat window. The chatbot answers by itself with an AI language model.</p>

      {none && <p className="mb-5 text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3" role="alert">No AI model is set up, so no chatbot can answer. Add an API key (see CHATBOT_LLM_URL / ANTHROPIC_API_KEY in the server settings).</p>}
      {onlyFree && <p className="mb-5 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3" role="status">Only the free hosted AI model is available. It is fine for trying things, but it answers slowly and allows about one question every few seconds per server - add a paid key (CHATBOT_LLM_URL / CHATBOT_LLM_KEY, or ANTHROPIC_API_KEY) before clients use the API.</p>}
      {status && !none && !onlyFree && <p className="mb-5 text-xs text-gray-500">AI models in use, in order: {status.providers.map((p) => `${p.name} (${p.model})`).join(", ")}.</p>}

      <div className="grid lg:grid-cols-[240px_1fr] gap-6 items-start">
        <div className="space-y-2">
          <button type="button" className={`${btn} w-full justify-center`} onClick={() => setSelected("new")}><FiPlus /> New chatbot</button>
          {bots === null && <p className="text-sm text-gray-500">Loading...</p>}
          {(bots || []).map((b) => (
            <button key={b.id} type="button" onClick={() => setSelected(b.id)} aria-current={selected === b.id}
              className={`w-full text-left rounded-lg px-3 py-2.5 border text-sm ${selected === b.id ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-white/30"}`}>
              <div className="font-medium flex items-center gap-2">{b.name}{b.is_demo && <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-light/20 text-accent-light">DEMO</span>}{!b.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-200">OFF</span>}</div>
              <div className="text-[11px] text-gray-500">{b.client_name || "No client"} - {b.usage.messages_today} today</div>
            </button>
          ))}
        </div>

        {selected === null && bots && !bots.length && <div className={`${panel} text-center text-gray-500`}>No chatbots yet. Press "New chatbot".</div>}
        {selected !== null && (
          <div className="space-y-6">
            <form onSubmit={save} className={`${panel} space-y-4`}>
              <h3 className="font-semibold">{selected === "new" ? "New chatbot" : "Chatbot settings"}</h3>
              <div>
                <label className={label} htmlFor="cb-kind">Type</label>
                <select id="cb-kind" className={field} value={form.kind} onChange={(e) => set("kind", e.target.value)}>
                  <option value="general">Chatbot - answers questions in general, using the notes below</option>
                  <option value="support">Customer support assistant - answers only from a knowledge base, hands over to your team, makes tickets</option>
                  <option value="sales">Sales and lead generation bot - qualifies visitors, scores leads, books calls, alerts your team</option>
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={label} htmlFor="cb-name">Name (shown in the chat)</label><input id="cb-name" className={field} value={form.name} onChange={(e) => set("name", e.target.value)} required maxLength={100} /></div>
                <div><label className={label} htmlFor="cb-client">Client (for your own notes)</label><input id="cb-client" className={field} value={form.client_name} onChange={(e) => set("client_name", e.target.value)} maxLength={150} /></div>
              </div>
              <div>
                <label className={label} htmlFor="cb-instr">What the chatbot knows and how it should behave</label>
                <textarea id="cb-instr" className={`${field} h-44`} value={form.instructions} onChange={(e) => set("instructions", e.target.value)}
                  placeholder={"Write it like a briefing for a new employee. For example:\nYou answer for Blue Door Pizza. Opening hours: 11am-11pm every day. Delivery is free over $20. We have vegetarian and halal options. Phone: ...\nBe friendly and short. If someone wants to order, ask them to call."} />
                <p className="text-[11px] text-gray-500 mt-1">The AI answers by itself from this and from what it already knows. It is told never to invent facts about the business that are not written here.</p>
              </div>
              <div><label className={label} htmlFor="cb-greet">First message shown in the chat window</label><input id="cb-greet" className={field} value={form.greeting} onChange={(e) => set("greeting", e.target.value)} maxLength={300} /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className={label} htmlFor="cb-lang">Language</label>
                  <select id="cb-lang" className={field} value={form.language} onChange={(e) => set("language", e.target.value)}>
                    {SPEECH_LANGUAGES.map(([c, n]) => <option key={c} value={c}>{c === "auto" ? "Same as the customer" : n}</option>)}
                  </select></div>
                <div><label className={label} htmlFor="cb-temp">Creativity (0 - 1.2)</label><input id="cb-temp" type="number" step="0.1" min="0" max="1.2" className={field} value={form.temperature} onChange={(e) => set("temperature", Number(e.target.value))} /></div>
                <div><label className={label} htmlFor="cb-color">Colour</label><input id="cb-color" type="color" className={`${field} h-10 p-1`} value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} /></div>
                <div><label className={label} htmlFor="cb-tokens">Longest answer (tokens)</label><input id="cb-tokens" type="number" min="50" max="4000" className={field} value={form.max_reply_tokens} onChange={(e) => set("max_reply_tokens", Number(e.target.value))} /></div>
                <div><label className={label} htmlFor="cb-hist">Remembers (last exchanges)</label><input id="cb-hist" type="number" min="0" max="30" className={field} value={form.history_turns} onChange={(e) => set("history_turns", Number(e.target.value))} /></div>
                <div><label className={label} htmlFor="cb-min">Questions per minute (0 = no limit)</label><input id="cb-min" type="number" min="0" className={field} value={form.per_minute_limit} onChange={(e) => set("per_minute_limit", Number(e.target.value))} /></div>
                <div><label className={label} htmlFor="cb-day">Questions per day (0 = no limit)</label><input id="cb-day" type="number" min="0" className={field} value={form.daily_limit} onChange={(e) => set("daily_limit", Number(e.target.value))} /></div>
              </div>
              <div>
                <label className={label} htmlFor="cb-origins">Websites allowed to show the chat window (one per line, e.g. https://acme.com)</label>
                <textarea id="cb-origins" className={`${field} h-20`} value={form.allowed_origins} onChange={(e) => set("allowed_origins", e.target.value)} placeholder="https://client-website.com" />
                <p className="text-[11px] text-gray-500 mt-1">Only these websites can use the chat window's public key. Use * to allow any website. API keys are not affected.</p>
              </div>
              {form.kind === "sales" && <SalesSettings value={form.sales} onChange={(v) => set("sales", v)} bot={bot} />}
              <div className="flex flex-wrap gap-6 text-sm">
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Switched on</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.is_demo} onChange={(e) => set("is_demo", e.target.checked)} /> The demo on the website's chatbot page (only one)</label>
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg bg-brand-gradient font-semibold text-sm disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
                {bot && <button type="button" className={btn} onClick={remove}><FiTrash2 /> Delete</button>}
                {message && <span className={`text-sm ${message === "Saved." ? "text-emerald-300" : "text-red-300"}`} role="status">{message}</span>}
              </div>
            </form>

            {bot && (
              <>
                <div className="grid sm:grid-cols-4 gap-3">
                  {[["Today", bot.usage.messages_today], ["This month", bot.usage.messages_this_month], ["All time", bot.usage.messages_total], ["Conversations", bot.usage.conversations]].map(([t, v]) => (
                    <div key={t} className={`${panel} py-3`}><div className="text-xs text-gray-400">{t}</div><div className="text-2xl font-bold tabular-nums">{v}</div></div>
                  ))}
                </div>
                {bot.kind === "support" && <InsightsPanel bot={bot} />}
                {bot.kind === "sales" && <FunnelPanel bot={bot} />}
                {bot.kind === "sales" && <LeadsPanel bot={bot} />}
                {bot.kind === "sales" && <MeetingsPanel bot={bot} />}
                {(bot.kind === "support" || bot.kind === "sales") && <KnowledgePanel bot={bot} />}
                {bot.kind === "support" && <TicketsPanel bot={bot} />}
                <ChannelsPanel bot={bot} />
                <Keys bot={bot} reload={load} />
                <div className={panel}>
                  <h3 className="font-semibold mb-1">Chat window for a website</h3>
                  <p className="text-xs text-gray-400 mb-3">One line before &lt;/body&gt;. It works only on the websites listed above. Public key: <code>{bot.widget_key}</code> <button type="button" className={`${btn} ml-2`} onClick={newWidgetKey}><FiRefreshCw /> New key</button></p>
                  <div className="relative"><pre className="rounded-lg bg-black/40 border border-navy-border p-3 pr-24 overflow-x-auto text-xs text-gray-200">{samples.embed}</pre><div className="absolute top-2 right-2"><Copy text={samples.embed} /></div></div>
                  <h3 className="font-semibold mt-5 mb-2">API example for the client</h3>
                  <div className="relative"><pre className="rounded-lg bg-black/40 border border-navy-border p-3 pr-24 overflow-x-auto text-xs text-gray-200">{samples.curl}</pre><div className="absolute top-2 right-2"><Copy text={samples.curl} /></div></div>
                  <p className="text-[11px] text-gray-500 mt-2">Documentation for clients (Python, PHP, JavaScript, errors): the Custom AI Chatbot page of the website.</p>
                </div>
                <TestChat bot={bot} status={status} />
                <Conversations bot={bot} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
